import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TanTable, BulkEditMeta } from '../../components/TanTable';
import { ColumnDef } from '@tanstack/react-table';
import api from '../../services/api';
import { deletedRowClass } from '../../utils/deletedRow';
import { PencilIcon, CheckIcon, XMarkIcon, ArrowPathIcon, TrashIcon, ArrowUturnLeftIcon, DocumentDuplicateIcon, ArrowUpTrayIcon, PlusIcon } from '@heroicons/react/24/outline';
import { MapPinIcon } from '@heroicons/react/24/solid';
import ChassisLocationMapModal from '../../components/ChassisLocationMapModal';
import { Tooltip } from '../../components/ui/Tooltip';
import { Drawer } from '../../components/ui/Drawer';
import Notification, { NotificationContainer } from '../../components/Notification';
import Toggle from '../../components/Toggle';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import InfoModal from '../../components/common/InfoModal';
import ExcelJS from 'exceljs';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import PermissionsContainer from '../../components/PermissionsContainer';
import { PermissionAwareInput, PermissionAwareButton, PermissionAwareSelect, PermissionAwareTextarea } from '../../contexts/PermissionAccessContext';

interface EquipmentRow {
  'Unit #': string;
  'In Service': string;
  'Category': string;
  'Market': string;
  'Year': string;
  'Licence': string;
  'State': string;
  'VIN': string;
  'Make': string;
  'Model': string;
  'In Service Date': string;
  'Out of Service Date': string;
  'Condition': string;
  'Lessor': string;
  'Lease Rate': string;
  'Lease Number': string;
  'Lease Crunch Tag': string;
  'Lease Start Date': string;
  'Lease End Date': string;
  'Period': string;
  'Terms': string;
  'Financed?': string;
  'Bank Name': string;
  'Refurbished': string;
  'GPS': string;
  'Title': string;
  'Tri-Axle': string;
  'Air Infl.': string;
  'Value': string;
  'Premium': string;
  'Market Value': string;
  'Tire Size': string;
  'Geir': string;
  'Customer Dedicated': string;
  'Tare Weight': string;
  'Reg Eff': string;
  'Reg Exp': string;
  'Comments': string;
  'Company': string;
  'Whse Dedicated': string;
  'Date Acquired': string;
  'Date Terminated': string;
  'Last Inspection': string;
  'Inspection Frequency': string;
  'Next Inspection': string;
  'Customer Locations'?: string[];
  _customerLocationLabels?: string[];
  'Deleted'?: string | null;
  recno?: number;
  radar_latitude?: number | null;
  radar_longitude?: number | null;
  radar_last_reported?: string | null;
}

interface SelectOption {
  value: string;
  label: string;
}

const EquipmentList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const defaultSearch = searchParams.get('search') || '';
  const { setDispatchButton } = useDispatchFooter();
  const [data, setData] = useState<EquipmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState<string>('');
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const editFormDataRef = useRef<EquipmentRow | null>(null);
  const [, forceUpdate] = useState({});
  const [errorNotification, setErrorNotification] = useState<{ title: string; message: string } | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [locationOptions, setLocationOptions] = useState<SelectOption[]>([]);
  const [customerLocationOptions, setCustomerLocationOptions] = useState<MultiSelectOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [savingRowId, setSavingRowId] = useState<string | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [mapModalData, setMapModalData] = useState<{ unit: string; lat: number; lng: number } | null>(null);

  // Duplicate modal state
  const [duplicateModal, setDuplicateModal] = useState<{
    isOpen: boolean;
    sourceRow: EquipmentRow | null;
    newUnitNumber: string;
  }>({
    isOpen: false,
    sourceRow: null,
    newUnitNumber: '',
  });

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Import modal state
  const [importModal, setImportModal] = useState<{
    isOpen: boolean;
    isImporting: boolean;
    progress: {
      totalRows: number;
      processedRows: number;
      currentUnit: string;
      stage: 'reading' | 'uploading' | 'processing' | 'done';
    } | null;
    results: Array<{
      unit_number: string;
      success: boolean;
      message: string;
      fields_updated: number;
      created?: boolean;
    }> | null;
    summary: {
      total_rows: number;
      total_updated: number;
      total_created: number;
      total_errors: number;
    } | null;
  }>({
    isOpen: false,
    isImporting: false,
    progress: null,
    results: null,
    summary: null,
  });

  // Add Equipment drawer state
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [addFormData, setAddFormData] = useState<Record<string, string>>({});
  const [isAddSaving, setIsAddSaving] = useState(false);
  const [successNotification, setSuccessNotification] = useState<{ title: string; message: string } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDispatchButton(
      <Tooltip content="Import from Excel" placement="top">
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-accent-fg bg-accent hover:bg-accent-hover rounded-md transition-colors"
        >
          <ArrowUpTrayIcon className="h-4 w-4" />
          Import Excel
        </button>
      </Tooltip>
    );

    // Clear footer button on unmount
    return () => {
      setDispatchButton(null);
    };
  }, [setDispatchButton]);

  // Handle file selection for import
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setImportModal(prev => ({
      ...prev,
      isOpen: true,
      isImporting: true,
      progress: { totalRows: 0, processedRows: 0, currentUnit: '', stage: 'reading' },
      results: null,
      summary: null
    }));

    try {
      // Read the Excel file
      setImportModal(prev => ({
        ...prev,
        progress: { ...prev.progress!, stage: 'reading', currentUnit: 'Reading Excel file...' }
      }));

      const arrayBuffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      // Get the first sheet
      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        throw new Error('No worksheet found in Excel file');
      }

      // Convert to JSON - first row becomes keys (headers)
      const jsonData: Array<Record<string, any>> = [];
      const headers: string[] = [];

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) {
          // First row is headers
          row.eachCell((cell, colNumber) => {
            headers[colNumber - 1] = cell.value?.toString() || `Column${colNumber}`;
          });
        } else {
          // Data rows
          const rowData: Record<string, any> = {};
          headers.forEach((header, index) => {
            const cell = row.getCell(index + 1);
            rowData[header] = cell.value ?? '';
          });
          jsonData.push(rowData);
        }
      });

      if (jsonData.length === 0) {
        setImportModal(prev => ({
          ...prev,
          isImporting: false,
          progress: null,
          results: [{
            unit_number: 'N/A',
            success: false,
            message: 'No data found in the Excel file',
            fields_updated: 0
          }],
          summary: { total_rows: 0, total_updated: 0, total_created: 0, total_errors: 1 }
        }));
        return;
      }

      // Update progress with total rows
      setImportModal(prev => ({
        ...prev,
        progress: {
          totalRows: jsonData.length,
          processedRows: 0,
          currentUnit: `Found ${jsonData.length} rows`,
          stage: 'uploading'
        }
      }));

      // Small delay to show the count
      await new Promise(resolve => setTimeout(resolve, 300));

      // Show uploading status
      setImportModal(prev => ({
        ...prev,
        progress: {
          ...prev.progress!,
          stage: 'processing',
          currentUnit: 'Sending to server...'
        }
      }));

      // Send to backend
      const response = await api.post('/equipment/import', { rows: jsonData });

      // Show completion
      setImportModal(prev => ({
        ...prev,
        progress: {
          totalRows: response.data.total_rows,
          processedRows: response.data.total_rows,
          currentUnit: 'Complete!',
          stage: 'done'
        }
      }));

      // Small delay before showing results
      await new Promise(resolve => setTimeout(resolve, 500));

      setImportModal(prev => ({
        ...prev,
        isImporting: false,
        progress: null,
        results: response.data.results,
        summary: {
          total_rows: response.data.total_rows,
          total_updated: response.data.total_updated,
          total_created: response.data.total_created || 0,
          total_errors: response.data.total_errors,
        }
      }));

      // Reload data if any updates or creates were made
      if (response.data.total_updated > 0 || response.data.total_created > 0) {
        const params = showDeleted ? { deleted: 'Y' } : { deleted: 'N' };
        const dataResponse = await api.get('/equipment/list', { params });
        setData(dataResponse.data.data || []);
      }

    } catch (error: any) {
      console.error('Error importing equipment:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setImportModal(prev => ({
        ...prev,
        isImporting: false,
        progress: null,
        results: [{
          unit_number: 'N/A',
          success: false,
          message: `Import failed: ${errorMessage}`,
          fields_updated: 0
        }],
        summary: { total_rows: 0, total_updated: 0, total_created: 0, total_errors: 1 }
      }));
    }
  }, [showDeleted]);

  // Load dedicated locations for a specific equipment
  const loadDedicatedLocations = async (unitNumber: string): Promise<string[]> => {
    try {
      const response = await api.get(`/equipment/${unitNumber}/dedicated-locations`);
      const locations = response.data.data || [];
      return locations.map((loc: MultiSelectOption) => loc.value);
    } catch (error) {
      console.error('Error loading dedicated locations:', error);
      return [];
    }
  };

  // Handle edit button click
  const handleEditRow = useCallback(async (row: EquipmentRow) => {
    setEditingRowId(row['Unit #']);
    const dedicatedLocationValues = await loadDedicatedLocations(row['Unit #']);
    editFormDataRef.current = { ...row, 'Customer Locations': dedicatedLocationValues };
    forceUpdate({});
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingRowId(null);
    editFormDataRef.current = null;
  }, []);

  // Delete (mark as deleted) handler
  const handleDelete = useCallback((row: EquipmentRow) => {
    setConfirmModal({
      isOpen: true,
      title: 'Confirm Delete',
      message: `Are you sure you want to mark equipment ${row['Unit #']} as deleted?`,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));

        try {
          // Update the Deleted field to 'Y' using recno (primary key) for unique identification
          await api.patch(`/equipment/by-recno/${row.recno}`, {
            field: 'Deleted',
            value: 'Y'
          });

          // Update local data using recno
          setData(prev => prev.map(item =>
            item.recno === row.recno
              ? { ...item, 'Deleted': 'Y' }
              : item
          ));

          // If we're not showing deleted items, remove it from the list
          if (!showDeleted) {
            setData(prev => prev.filter(item => item.recno !== row.recno));
          }
        } catch (error: any) {
          console.error('Error marking equipment as deleted:', error);
          const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
          setErrorNotification({
            title: 'Failed to Delete',
            message: `Could not mark equipment ${row['Unit #']} as deleted: ${errorMessage}`
          });
        }
      }
    });
  }, [showDeleted]);

  // Restore (mark as not deleted) handler
  const handleRestore = useCallback((row: EquipmentRow) => {
    setConfirmModal({
      isOpen: true,
      title: 'Confirm Restore',
      message: `Are you sure you want to restore equipment ${row['Unit #']}?`,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));

        try {
          // Update the Deleted field to 'N' using recno (primary key)
          await api.patch(`/equipment/by-recno/${row.recno}`, {
            field: 'Deleted',
            value: 'N'
          });

          // Update local data to mark as not deleted using recno
          setData(prev => prev.map(item =>
            item.recno === row.recno
              ? { ...item, 'Deleted': 'N' }
              : item
          ));
        } catch (error: any) {
          console.error('Error restoring equipment:', error);
          const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
          setErrorNotification({
            title: 'Failed to Restore',
            message: `Could not restore equipment ${row['Unit #']}: ${errorMessage}`
          });
        }
      }
    });
  }, []);

  // Open duplicate modal
  const handleDuplicateClick = useCallback((row: EquipmentRow) => {
    setDuplicateModal({
      isOpen: true,
      sourceRow: row,
      newUnitNumber: `${row['Unit #']}-COPY`,
    });
  }, []);

  // Perform the duplicate operation
  const handleDuplicateConfirm = useCallback(async () => {
    if (!duplicateModal.sourceRow || !duplicateModal.newUnitNumber.trim()) return;

    setIsDuplicating(true);
    const sourceUnit = duplicateModal.sourceRow['Unit #'];
    const newUnit = duplicateModal.newUnitNumber.trim();

    try {
      const response = await api.post(`/equipment/${sourceUnit}/duplicate`, {
        new_unit_number: newUnit
      });

      const newRow = response.data.data as EquipmentRow;

      // Find the index of the source row and insert the new row directly after it
      setData(prev => {
        const sourceIndex = prev.findIndex(item => item['Unit #'] === sourceUnit);
        if (sourceIndex === -1) {
          // Source not found, just add to end
          return [...prev, newRow];
        }
        // Insert after source row
        const newData = [...prev];
        newData.splice(sourceIndex + 1, 0, newRow);
        return newData;
      });

      // Close the modal
      setDuplicateModal({ isOpen: false, sourceRow: null, newUnitNumber: '' });

      // Load dedicated locations for the new row and enter edit mode
      const dedicatedLocationValues = await loadDedicatedLocations(newUnit);
      setEditingRowId(newUnit);
      editFormDataRef.current = { ...newRow, 'Customer Locations': dedicatedLocationValues };
      forceUpdate({});

    } catch (error: any) {
      console.error('Error duplicating equipment:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({
        title: 'Failed to Duplicate',
        message: `Could not duplicate equipment ${sourceUnit}: ${errorMessage}`
      });
    } finally {
      setIsDuplicating(false);
    }
  }, [duplicateModal]);

  // Add New Equipment handlers
  const closeAddDrawer = () => {
    setIsAddDrawerOpen(false);
    setAddFormData({});
  };

  const handleAddEquipment = async () => {
    if (!addFormData['Unit #']?.trim()) {
      setErrorNotification({ title: 'Validation Error', message: 'Unit # is required.' });
      return;
    }
    if (!addFormData['Category']?.trim()) {
      setErrorNotification({ title: 'Validation Error', message: 'Category is required.' });
      return;
    }

    setIsAddSaving(true);
    try {
      const response = await api.post('/equipment', { data: addFormData });
      const newRow = response.data.data as EquipmentRow;
      setData(prev => [newRow, ...prev]);
      setSuccessNotification({ title: 'Equipment Created', message: `Equipment '${addFormData['Unit #']}' has been created.` });
      closeAddDrawer();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Create Equipment', message: msg });
    } finally {
      setIsAddSaving(false);
    }
  };

  // Save entire row to API
  const handleSaveRow = async () => {
    if (!editingRowId || !editFormDataRef.current || isSaving) return;

    const rowKey = editingRowId;
    const editFormData = editFormDataRef.current;
    const oldRow = data.find(row => row['Unit #'] === editingRowId);
    if (!oldRow || !oldRow.recno) return;

    const recno = oldRow.recno;
    setIsSaving(true);
    setSavingRowId(rowKey);

    try {
      // Update local data immediately (optimistic update)
      setData(prev => prev.map(row => {
        if (row['Unit #'] === rowKey) {
          return { ...editFormData };
        }
        return row;
      }));

      // Save all fields to API using recno (primary key) for unique identification
      const updatedFields = Object.keys(editFormData).filter(key => key !== 'Unit #' && key !== 'Customer Locations' && key !== 'recno' && key !== '_customerLocationLabels' && key !== 'Next Inspection');

      for (const field of updatedFields) {
        const newValue = editFormData[field as keyof EquipmentRow];
        const oldValue = oldRow[field as keyof EquipmentRow];

        // Only update if value changed
        if (newValue !== oldValue) {
          await api.patch(`/equipment/by-recno/${recno}`, {
            field: field,
            value: newValue
          });
        }
      }

      // Save customer locations separately (still uses unit number for this endpoint)
      const customerLocationValues = editFormData['Customer Locations'] || [];
      const locationRecnos = customerLocationValues.map(value => parseInt(value));
      await api.post(`/equipment/${rowKey}/dedicated-locations`, locationRecnos);

      console.log(`Saved all changes for ${rowKey} (recno: ${recno})`);
      setEditingRowId(null);
      editFormDataRef.current = null;
    } catch (error: any) {
      console.error('Error saving equipment data:', error);
      // Revert on error
      setData(prev => prev.map(row => {
        if (row['Unit #'] === rowKey) {
          return { ...oldRow };
        }
        return row;
      }));

      // Show error toast
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({
        title: 'Failed to Save Row',
        message: `Could not update equipment ${rowKey}: ${errorMessage}`
      });
    } finally {
      setIsSaving(false);
      setSavingRowId(null);
    }
  };

  const handleEquipmentBulkEdit = useCallback(async (columnId: string, rows: EquipmentRow[], value: unknown) => {
    // Optimistic update
    setData(prev => prev.map(row =>
      rows.some(r => r['Unit #'] === row['Unit #'])
        ? { ...row, [columnId]: value }
        : row
    ));
    // PATCH each row — same endpoint as single-row save
    for (const row of rows) {
      if (!row.recno) continue;
      try {
        await api.patch(`/equipment/by-recno/${row.recno}`, { field: columnId, value });
      } catch (error: any) {
        // Revert the optimistic update
        setData(prev => prev.map(r => {
          const original = rows.find(orig => orig['Unit #'] === r['Unit #']);
          return original ? { ...original } : r;
        }));
        const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
        setErrorNotification({ title: 'Bulk Edit Failed', message: `Could not update ${rows.length} rows: ${errorMessage}` });
        return;
      }
    }
  }, []);

  // Define columns for TanTable with inline editing
  const columns = useMemo<ColumnDef<EquipmentRow>[]>(
    () => [
      {
        accessorKey: 'Unit #',
        header: 'Unit #',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
      },
      {
        accessorKey: 'In Service',
        header: 'In Service',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [
              { value: 'Y', label: 'Y' },
              { value: 'N', label: 'N' },
              { value: 'STACKED', label: 'STACKED' },
              { value: 'STORAGE', label: 'STORAGE' },
            ],
          } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <select
                value={editFormData['In Service'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'In Service': e.target.value };
                    forceUpdate({});
                  }
                }}
                disabled={isRowSaving}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select...</option>
                <option value="Y">Y</option>
                <option value="N">N</option>
                <option value="STACKED">STACKED</option>
                <option value="STORAGE">STORAGE</option>
              </select>
            );
          }
          return <span>{row.original['In Service']}</span>;
        }
      },
      {
        accessorKey: 'Category',
        header: 'Category',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [
              { value: 'CHASSIS', label: 'CHASSIS' },
              { value: 'FLATBED', label: 'FLATBED' },
              { value: 'MULE', label: 'MULE' },
              { value: 'VAN', label: 'VAN' },
            ],
          } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <select
                value={editFormData['Category'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Category': e.target.value };
                    forceUpdate({});
                  }
                }}
                disabled={isRowSaving}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select...</option>
                <option value="CHASSIS">CHASSIS</option>
                <option value="FLATBED">FLATBED</option>
                <option value="MULE">MULE</option>
                <option value="VAN">VAN</option>
              </select>
            );
          }
          return <span>{row.original['Category']}</span>;
        }
      },
      {
        accessorKey: 'Market',
        header: 'Market',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 150,
        meta: {
          exportValue: (row: EquipmentRow) => {
            // Custom export value function to show location name instead of ID
            const marketValue = String(row['Market'] || '');
            if (!marketValue || marketValue === '0') {
              return '';
            }
            const locationName = locationOptions.find(loc => loc.value === marketValue)?.label;
            return locationName || marketValue;
          },
          filterValueLabel: (value: string) => {
            // Transform Market ID to location name for filter dropdown display
            if (!value || value === '0') {
              return '';
            }
            const locationName = locationOptions.find(loc => loc.value === value)?.label;
            return locationName || value;
          },
          bulkEdit: {
            type: 'dropdown',
            options: locationOptions,
          } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <select
                value={editFormData['Market'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Market': e.target.value };
                    forceUpdate({});
                  }
                }}
                disabled={isRowSaving}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select...</option>
                {locationOptions.map(loc => (
                  <option key={loc.value} value={loc.value}>{loc.label}</option>
                ))}
              </select>
            );
          }
          // Display location name instead of ID, show blank for 0 or empty
          const marketValue = String(row.original['Market'] || '');
          if (!marketValue || marketValue === '0') {
            return <span></span>;
          }
          const locationName = locationOptions.find(loc => loc.value === marketValue)?.label || row.original['Market'];
          return <span>{locationName}</span>;
        }
      },
      {
        accessorKey: 'Year',
        header: 'Year',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 100,
        meta: { bulkEdit: { type: 'number' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="number"
                value={editFormData['Year'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Year': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['Year']}</span>;
        }
      },
      {
        accessorKey: 'Licence',
        header: 'Licence',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['Licence'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Licence': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['Licence']}</span>;
        }
      },
      {
        accessorKey: 'State',
        header: 'State',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 100,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['State'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'State': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['State']}</span>;
        }
      },
      {
        accessorKey: 'VIN',
        header: 'VIN',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 180,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['VIN'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'VIN': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['VIN']}</span>;
        }
      },
      {
        accessorKey: 'Make',
        header: 'Make',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['Make'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Make': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['Make']}</span>;
        }
      },
      {
        accessorKey: 'Model',
        header: 'Model',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['Model'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Model': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['Model']}</span>;
        }
      },
      {
        accessorKey: 'In Service Date',
        header: 'In Service Date',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 140,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const dateValue = editFormData['In Service Date'] && editFormData['In Service Date'] !== '0000-00-00'
              ? String(editFormData['In Service Date']).split('T')[0]
              : '';
            return (
              <input
                type="date"
                value={dateValue}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'In Service Date': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          const date = row.original['In Service Date'];
          if (!date || date === '0000-00-00') return '';
          if ((date as any) instanceof Date) return (date as unknown as Date).toISOString().split('T')[0];
          return String(date);
        }
      },
      {
        accessorKey: 'Out of Service Date',
        header: 'Out of Service Date',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 160,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const dateValue = editFormData['Out of Service Date'] && editFormData['Out of Service Date'] !== '0000-00-00'
              ? String(editFormData['Out of Service Date']).split('T')[0]
              : '';
            return (
              <input
                type="date"
                value={dateValue}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Out of Service Date': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          const date = row.original['Out of Service Date'];
          if (!date || date === '0000-00-00') return '';
          if ((date as any) instanceof Date) return (date as unknown as Date).toISOString().split('T')[0];
          return String(date);
        }
      },
      {
        accessorKey: 'Last Inspection',
        header: 'Last Inspection',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 140,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const dateValue = editFormData['Last Inspection'] && editFormData['Last Inspection'] !== '0000-00-00'
              ? String(editFormData['Last Inspection']).split('T')[0]
              : '';
            return (
              <input
                type="date"
                value={dateValue}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Last Inspection': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          const date = row.original['Last Inspection'];
          if (!date || date === '0000-00-00') return '';
          if ((date as any) instanceof Date) return (date as unknown as Date).toISOString().split('T')[0];
          return String(date);
        }
      },
      {
        accessorKey: 'Inspection Frequency',
        header: 'Inspec Freq',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 140,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="number"
                min={0}
                value={editFormData['Inspection Frequency'] ?? ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Inspection Frequency': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          const freq = row.original['Inspection Frequency'];
          if (freq === null || freq === undefined || freq === '' || String(freq) === '0') return '';
          return <span>{`${String(freq)} Days`}</span>;
        }
      },
      {
        accessorKey: 'Next Inspection',
        header: 'Next Inspection',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 140,
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;
          const lastRaw = isEditing && editFormData ? editFormData['Last Inspection'] : row.original['Last Inspection'];
          const freqRaw = isEditing && editFormData ? editFormData['Inspection Frequency'] : row.original['Inspection Frequency'];

          if (!lastRaw || lastRaw === '0000-00-00') return '';
          const freqDays = parseInt(String(freqRaw), 10);
          if (!freqDays || freqDays <= 0) return '';

          const lastStr = String(lastRaw).split('T')[0];
          const lastDate = new Date(lastStr + 'T00:00:00');
          if (isNaN(lastDate.getTime())) return '';
          lastDate.setDate(lastDate.getDate() + freqDays);
          const yyyy = lastDate.getFullYear();
          const mm = String(lastDate.getMonth() + 1).padStart(2, '0');
          const dd = String(lastDate.getDate()).padStart(2, '0');
          return <span>{`${yyyy}-${mm}-${dd}`}</span>;
        }
      },
      {
        accessorKey: 'Condition',
        header: 'Condition',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [
              { value: 'LEASED', label: 'LEASED' },
              { value: 'OWNED', label: 'OWNED' },
              { value: 'RENTAL', label: 'RENTAL' },
            ],
          } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <select
                value={editFormData['Condition'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Condition': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              >
                <option value="">Select...</option>
                <option value="LEASED">LEASED</option>
                <option value="OWNED">OWNED</option>
                <option value="RENTAL">RENTAL</option>
              </select>
            );
          }
          return <span>{row.original['Condition']}</span>;
        }
      },
      {
        accessorKey: 'Lessor',
        header: 'Lessor',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 150,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [
              { value: 'GWI', label: 'GWI' },
              { value: 'PENN LEASING', label: 'PENN LEASING' },
              { value: 'TIP', label: 'TIP' },
              { value: 'XTRA', label: 'XTRA' },
              { value: 'TICO', label: 'TICO' },
              { value: 'BRIGGS', label: 'BRIGGS' },
              { value: 'MERRIMAK', label: 'MERRIMAK' },
              { value: 'PREMIER', label: 'PREMIER' },
              { value: 'TRAK', label: 'TRAK' },
            ],
          } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <select
                value={editFormData['Lessor'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Lessor': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              >
                <option value="">Select...</option>
                <option value="GWI">GWI</option>
                <option value="PENN LEASING">PENN LEASING</option>
                <option value="TIP">TIP</option>
                <option value="XTRA">XTRA</option>
                <option value="TICO">TICO</option>
                <option value="BRIGGS">BRIGGS</option>
                <option value="MERRIMAK">MERRIMAK</option>
                <option value="PREMIER">PREMIER</option>
                <option value="TRAK">TRAK</option>
              </select>
            );
          }
          return <span>{row.original['Lessor']}</span>;
        }
      },
      {
        accessorKey: 'Lease Rate',
        header: 'Lease Rate',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: { bulkEdit: { type: 'number' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="number"
                step="0.01"
                value={editFormData['Lease Rate'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Lease Rate': e.target.value };
                    forceUpdate({});
                  }
                }}
                disabled={isRowSaving}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading disabled:opacity-50 disabled:cursor-not-allowed"
              />
            );
          }
          return <span>{row.original['Lease Rate']}</span>;
        }
      },
      {
        accessorKey: 'Lease Number',
        header: 'Lease Number',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 140,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['Lease Number'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Lease Number': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['Lease Number']}</span>;
        }
      },
      {
        accessorKey: 'Lease Crunch Tag',
        header: 'Lease Crunch Tag',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 160,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['Lease Crunch Tag'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Lease Crunch Tag': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['Lease Crunch Tag']}</span>;
        }
      },
      {
        accessorKey: 'Lease Start Date',
        header: 'Lease Start Date',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 150,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const dateValue = editFormData['Lease Start Date'] && editFormData['Lease Start Date'] !== '0000-00-00'
              ? String(editFormData['Lease Start Date']).split('T')[0]
              : '';
            return (
              <input
                type="date"
                value={dateValue}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Lease Start Date': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          const date = row.original['Lease Start Date'];
          if (!date || date === '0000-00-00') return '';
          if ((date as any) instanceof Date) return (date as unknown as Date).toISOString().split('T')[0];
          return String(date);
        }
      },
      {
        accessorKey: 'Lease End Date',
        header: 'Lease End Date',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 150,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const dateValue = editFormData['Lease End Date'] && editFormData['Lease End Date'] !== '0000-00-00'
              ? String(editFormData['Lease End Date']).split('T')[0]
              : '';
            return (
              <input
                type="date"
                value={dateValue}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Lease End Date': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          const date = row.original['Lease End Date'];
          if (!date || date === '0000-00-00') return '';
          if ((date as any) instanceof Date) return (date as unknown as Date).toISOString().split('T')[0];
          return String(date);
        }
      },
      {
        accessorKey: 'Period',
        header: 'Period',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 100,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['Period'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Period': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['Period']}</span>;
        }
      },
      {
        accessorKey: 'Terms',
        header: 'Terms',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 100,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['Terms'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Terms': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['Terms']}</span>;
        }
      },
      {
        accessorKey: 'Financed?',
        header: 'Financed?',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [{ value: 'Y', label: 'Y' }, { value: 'N', label: 'N' }],
          } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const isChecked = editFormData['Financed?'] === 'Y';
            return (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">N</span>
                <Toggle
                  variant="with-icons"
                  size="sm"
                  checked={isChecked}
                  onChange={(checked) => {
                    if (editFormDataRef.current) {
                      editFormDataRef.current = { ...editFormDataRef.current, 'Financed?': checked ? 'Y' : 'N' };
                      forceUpdate({});
                    }
                  }}
                  disabled={isRowSaving}
                />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Y</span>
              </div>
            );
          }
          return <span>{row.original['Financed?']}</span>;
        }
      },
      {
        accessorKey: 'Bank Name',
        header: 'Bank Name',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 150,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['Bank Name'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Bank Name': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['Bank Name']}</span>;
        }
      },
      {
        id: 'GL Code',
        header: 'GL Code',
        // Blank accounting column for completion in the exported workbook.
        accessorFn: () => '',
        enableSorting: false,
        enableColumnFilter: false,
        size: 120,
      },
      {
        accessorKey: 'Refurbished',
        header: 'Refurbished',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [{ value: 'Y', label: 'Y' }, { value: 'N', label: 'N' }],
          } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const isChecked = editFormData['Refurbished'] === 'Y';
            return (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">N</span>
                <Toggle
                  variant="with-icons"
                  size="sm"
                  checked={isChecked}
                  onChange={(checked) => {
                    if (editFormDataRef.current) {
                      editFormDataRef.current = { ...editFormDataRef.current, 'Refurbished': checked ? 'Y' : 'N' };
                      forceUpdate({});
                    }
                  }}
                  disabled={isRowSaving}
                />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Y</span>
              </div>
            );
          }
          return <span>{row.original['Refurbished']}</span>;
        }
      },
      {
        accessorKey: 'GPS',
        header: 'GPS',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [
              { value: 'NONE', label: 'NONE' },
              { value: 'BLACKBERRY', label: 'BLACKBERRY' },
              { value: 'POWERFLEET', label: 'POWERFLEET' },
              { value: 'SAMSARA', label: 'SAMSARA' },
            ],
          } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <select
                value={editFormData['GPS'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'GPS': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              >
                <option value="">Select...</option>
                <option value="NONE">NONE</option>
                <option value="BLACKBERRY">BLACKBERRY</option>
                <option value="POWERFLEET">POWERFLEET</option>
                <option value="SAMSARA">SAMSARA</option>
              </select>
            );
          }
          return <span>{row.original['GPS']}</span>;
        }
      },
      {
        accessorKey: 'Title',
        header: 'Title',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 100,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <select
                value={editFormData['Title'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Title': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              >
                <option value="">Select...</option>
                <option value="OWNED">OWNED</option>
                <option value="LEASED">LEASED</option>
              </select>
            );
          }
          return <span>{row.original['Title']}</span>;
        }
      },
      {
        accessorKey: 'Tri-Axle',
        header: 'Tri-Axle',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 100,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [{ value: 'Y', label: 'Y' }, { value: 'N', label: 'N' }],
          } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const isChecked = editFormData['Tri-Axle'] === 'Y';
            return (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">N</span>
                <Toggle
                  variant="with-icons"
                  size="sm"
                  checked={isChecked}
                  onChange={(checked) => {
                    if (editFormDataRef.current) {
                      editFormDataRef.current = { ...editFormDataRef.current, 'Tri-Axle': checked ? 'Y' : 'N' };
                      forceUpdate({});
                    }
                  }}
                  disabled={isRowSaving}
                />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Y</span>
              </div>
            );
          }
          return <span>{row.original['Tri-Axle']}</span>;
        }
      },
      {
        accessorKey: 'Air Infl.',
        header: 'Air Infl.',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 100,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [{ value: 'Y', label: 'Y' }, { value: 'N', label: 'N' }],
          } satisfies BulkEditMeta,
        },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const isChecked = editFormData['Air Infl.'] === 'Y';
            return (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">N</span>
                <Toggle
                  variant="with-icons"
                  size="sm"
                  checked={isChecked}
                  onChange={(checked) => {
                    if (editFormDataRef.current) {
                      editFormDataRef.current = { ...editFormDataRef.current, 'Air Infl.': checked ? 'Y' : 'N' };
                      forceUpdate({});
                    }
                  }}
                  disabled={isRowSaving}
                />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Y</span>
              </div>
            );
          }
          return <span>{row.original['Air Infl.']}</span>;
        }
      },
      {
        accessorKey: 'Value',
        header: 'Value',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: { bulkEdit: { type: 'number' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="number"
                step="0.01"
                value={editFormData['Value'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Value': e.target.value };
                    forceUpdate({});
                  }
                }}
                disabled={isRowSaving}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading disabled:opacity-50 disabled:cursor-not-allowed"
              />
            );
          }
          return <span>{row.original['Value']}</span>;
        }
      },
      {
        accessorKey: 'Premium',
        header: 'Premium',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: { bulkEdit: { type: 'number' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="number"
                step="0.01"
                value={editFormData['Premium'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Premium': e.target.value };
                    forceUpdate({});
                  }
                }}
                disabled={isRowSaving}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading disabled:opacity-50 disabled:cursor-not-allowed"
              />
            );
          }
          return <span>{row.original['Premium']}</span>;
        }
      },
      {
        accessorKey: 'Market Value',
        header: 'Purchase Price',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 140,
        meta: { bulkEdit: { type: 'number' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="number"
                step="0.01"
                value={editFormData['Market Value'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Market Value': e.target.value };
                    forceUpdate({});
                  }
                }}
                disabled={isRowSaving}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading disabled:opacity-50 disabled:cursor-not-allowed"
              />
            );
          }
          return <span>{row.original['Market Value']}</span>;
        }
      },
      {
        accessorKey: 'Tire Size',
        header: 'Tire Size',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['Tire Size'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Tire Size': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['Tire Size']}</span>;
        }
      },
      {
        accessorKey: 'Geir',
        header: 'Geir',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 100,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['Geir'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Geir': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['Geir']}</span>;
        }
      },
      {
        accessorKey: 'Customer Dedicated',
        header: 'Customer Dedicated',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 180,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['Customer Dedicated'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Customer Dedicated': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          return <span>{row.original['Customer Dedicated']}</span>;
        }
      },
      {
        accessorKey: 'Tare Weight',
        header: 'Tare Weight',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: { bulkEdit: { type: 'number' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="number"
                step="0.01"
                value={editFormData['Tare Weight'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Tare Weight': e.target.value };
                    forceUpdate({});
                  }
                }}
                disabled={isRowSaving}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading disabled:opacity-50 disabled:cursor-not-allowed"
              />
            );
          }
          return <span>{row.original['Tare Weight']}</span>;
        }
      },
      {
        accessorKey: 'Reg Eff',
        header: 'Reg Eff',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const dateValue = editFormData['Reg Eff'] && editFormData['Reg Eff'] !== '0000-00-00'
              ? String(editFormData['Reg Eff']).split('T')[0]
              : '';
            return (
              <input
                type="date"
                value={dateValue}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Reg Eff': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          const date = row.original['Reg Eff'];
          if (!date || date === '0000-00-00') return '';
          if ((date as any) instanceof Date) return (date as unknown as Date).toISOString().split('T')[0];
          return String(date);
        }
      },
      {
        accessorKey: 'Reg Exp',
        header: 'Reg Exp',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const dateValue = editFormData['Reg Exp'] && editFormData['Reg Exp'] !== '0000-00-00'
              ? String(editFormData['Reg Exp']).split('T')[0]
              : '';
            return (
              <input
                type="date"
                value={dateValue}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Reg Exp': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          const date = row.original['Reg Exp'];
          if (!date || date === '0000-00-00') return '';
          if ((date as any) instanceof Date) return (date as unknown as Date).toISOString().split('T')[0];
          return String(date);
        }
      },
      {
        accessorKey: 'Comments',
        header: 'Comments',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 250,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['Comments'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Comments': e.target.value };
                    forceUpdate({});
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
              />
            );
          }
          const comment = row.original.Comments || '';
          const truncated = comment.length > 100 ? comment.substring(0, 100) + '...' : comment;
          const needsTooltip = comment.length > 100;

          const content = <span>{truncated || '-'}</span>;

          if (needsTooltip) {
            return (
              <Tooltip content={comment} placement="top">
                {content}
              </Tooltip>
            );
          }

          return content;
        }
      },
      {
        accessorKey: 'Company',
        header: 'Company',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 150,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <input
                type="text"
                value={editFormData['Company'] || ''}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Company': e.target.value };
                    forceUpdate({});
                  }
                }}
                disabled={isRowSaving}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading disabled:opacity-50 disabled:cursor-not-allowed"
              />
            );
          }
          return <span>{row.original['Company']}</span>;
        }
      },
      {
        accessorKey: 'Whse Dedicated',
        header: 'Whse Dedicated',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 140,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const isChecked = editFormData['Whse Dedicated'] === 'Y';
            return (
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">N</span>
                <Toggle
                  variant="with-icons"
                  size="sm"
                  checked={isChecked}
                  onChange={(checked) => {
                    if (editFormDataRef.current) {
                      editFormDataRef.current = { ...editFormDataRef.current, 'Whse Dedicated': checked ? 'Y' : 'N' };
                      forceUpdate({});
                    }
                  }}
                  disabled={isRowSaving}
                />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Y</span>
              </div>
            );
          }
          return <span>{row.original['Whse Dedicated']}</span>;
        }
      },
      {
        accessorKey: 'Date Acquired',
        header: 'Date Acquired',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 140,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const dateValue = editFormData['Date Acquired'] && editFormData['Date Acquired'] !== '0000-00-00'
              ? String(editFormData['Date Acquired']).split('T')[0]
              : '';
            return (
              <input
                type="date"
                value={dateValue}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Date Acquired': e.target.value };
                    forceUpdate({});
                  }
                }}
                disabled={isRowSaving}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading disabled:opacity-50 disabled:cursor-not-allowed"
              />
            );
          }
          const date = row.original['Date Acquired'];
          if (!date || date === '0000-00-00') return '';
          if ((date as any) instanceof Date) return (date as unknown as Date).toISOString().split('T')[0];
          return String(date);
        }
      },
      {
        accessorKey: 'Date Terminated',
        header: 'Date Terminated',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 150,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const dateValue = editFormData['Date Terminated'] && editFormData['Date Terminated'] !== '0000-00-00'
              ? String(editFormData['Date Terminated']).split('T')[0]
              : '';
            return (
              <input
                type="date"
                value={dateValue}
                onChange={(e) => {
                  if (editFormDataRef.current) {
                    editFormDataRef.current = { ...editFormDataRef.current, 'Date Terminated': e.target.value };
                    forceUpdate({});
                  }
                }}
                disabled={isRowSaving}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading disabled:opacity-50 disabled:cursor-not-allowed"
              />
            );
          }
          const date = row.original['Date Terminated'];
          if (!date || date === '0000-00-00') return '';
          if ((date as any) instanceof Date) return (date as unknown as Date).toISOString().split('T')[0];
          return String(date);
        }
      },
      {
        id: 'Customer Locations',
        accessorFn: (row: any) => row._customerLocationLabels ?? [],
        header: 'Customer Locations',
        enableSorting: false,
        enableColumnFilter: true,
        filterFn: 'arrIncludes' as any,
        size: 300,
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            return (
              <div className="min-w-[300px]" onClick={(e) => e.stopPropagation()}>
                <MultiSelect
                  options={customerLocationOptions}
                  value={editFormData['Customer Locations'] || []}
                  onChange={(selectedValues) => {
                    if (editFormDataRef.current) {
                      editFormDataRef.current = { ...editFormDataRef.current, 'Customer Locations': selectedValues };
                      forceUpdate({});
                    }
                  }}
                  placeholder="Select customer locations..."
                  disabled={isRowSaving}
                  enableSearch={true}
                  searchPlaceholder="Search locations..."
                  usePortal={true}
                />
              </div>
            );
          }
          const locationValues = row.original['Customer Locations'] || [];
          if (locationValues.length === 0) return <span>-</span>;
          const displayLocations = locationValues.map(value => {
            const location = customerLocationOptions.find(opt => opt.value === value);
            return location ? location.label : value;
          });
          return (
            <div className="flex flex-wrap gap-1">
              {displayLocations.map((label, index) => (
                <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-info text-info-fg">
                  {label}
                </span>
              ))}
            </div>
          );
        }
      },
      ...(showDeleted ? [{
        accessorKey: 'Deleted' as any,
        header: 'Deleted',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes' as any,
        size: 120,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [{ value: 'Y', label: 'Y' }, { value: 'N', label: 'N' }],
          } satisfies BulkEditMeta,
        },
        cell: ({ row }: any) => {
          const isEditing = editingRowId === row.original['Unit #'];
          const isRowSaving = savingRowId === row.original['Unit #'];
          const editFormData = editFormDataRef.current;

          if (isEditing && editFormData) {
            const isChecked = editFormData['Deleted'] === 'Y';
            return (
              <div className="flex items-center gap-2" onClick={(e: any) => e.stopPropagation()}>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">N</span>
                <Toggle
                  variant="with-icons"
                  size="sm"
                  checked={isChecked}
                  onChange={(checked: boolean) => {
                    if (editFormDataRef.current) {
                      editFormDataRef.current = { ...editFormDataRef.current, 'Deleted': checked ? 'Y' : 'N' };
                      forceUpdate({});
                    }
                  }}
                  disabled={isRowSaving}
                />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Y</span>
              </div>
            );
          }
          // Show toggle for non-editing rows too
          const isChecked = row.original['Deleted'] === 'Y';
          return (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">N</span>
              <Toggle
                variant="with-icons"
                size="sm"
                checked={isChecked}
                onChange={() => {}} // Read-only toggle
                disabled={true}
              />
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Y</span>
            </div>
          );
        }
      }] : []),
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        enableColumnFilter: false,
        size: 130,
        cell: ({ row }) => {
          const isEditing = editingRowId === row.original['Unit #'];

          if (isEditing) {
            return (
              <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={handleSaveRow}
                  disabled={isSaving}
                  className={`p-1 transition-colors ${
                    isSaving
                      ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      : 'text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300'
                  }`}
                  title={isSaving ? "Saving..." : "Save"}
                >
                  {isSaving ? (
                    <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckIcon className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className={`p-1 transition-colors ${
                    isSaving
                      ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      : 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
                  }`}
                  title="Cancel"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            );
          }

          const isDeleted = row.original['Deleted'] === 'Y';

          return (
            <div className="flex gap-1 justify-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.original.radar_latitude && row.original.radar_longitude) {
                    setMapModalData({
                      unit: row.original['Unit #'],
                      lat: row.original.radar_latitude!,
                      lng: row.original.radar_longitude!,
                    });
                  }
                }}
                disabled={!row.original.radar_latitude || !row.original.radar_longitude}
                className={`p-1 transition-colors ${
                  row.original.radar_latitude && row.original.radar_longitude
                    ? 'text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 cursor-pointer'
                    : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                }`}
                title={row.original.radar_latitude ? "View Chassis Location" : "No location data"}
              >
                <MapPinIcon className="w-5 h-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditRow(row.original);
                }}
                className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                title="Edit Row"
              >
                <PencilIcon className="w-5 h-5" />
              </button>
{/* Duplicate button hidden for now
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDuplicateClick(row.original);
                }}
                className="p-1 text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 transition-colors"
                title="Duplicate"
              >
                <DocumentDuplicateIcon className="w-5 h-5" />
              </button>
*/}
              {isDeleted ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestore(row.original);
                  }}
                  className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 transition-colors"
                  title="Restore"
                >
                  <ArrowUturnLeftIcon className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(row.original);
                  }}
                  className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                  title="Mark as Deleted"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          );
        }
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationOptions, customerLocationOptions, editingRowId, isSaving, savingRowId, showDeleted]
  );

  const dataWithLocationLabels = useMemo(() => {
    if (customerLocationOptions.length === 0) return data;
    const labelById = new Map(customerLocationOptions.map(o => [o.value, o.label]));
    return data.map(row => ({
      ...row,
      _customerLocationLabels: (row['Customer Locations'] || [])
        .map(id => labelById.get(id) ?? String(id)),
    }));
  }, [data, customerLocationOptions]);

  // Load locations from API on mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const response = await api.get('/equipment/locations');
        setLocationOptions(response.data.data || []);
      } catch (error) {
        console.error('Error loading equipment locations:', error);
      }
    };

    const loadCustomerLocations = async () => {
      try {
        const response = await api.get('/equipment/customer-locations');
        setCustomerLocationOptions(response.data.data || []);
      } catch (error) {
        console.error('Error loading customer locations:', error);
      }
    };

    loadLocations();
    loadCustomerLocations();
  }, []);

  // Load all data from API on mount and when showDeleted changes
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const params = showDeleted ? { deleted: 'Y' } : { deleted: 'N' };
        const response = await api.get('/equipment/list', { params });
        setData(response.data.data || []);
        setSchema(response.data.schema);
      } catch (error) {
        console.error('Error loading equipment data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showDeleted]);

  return (
    <div className="h-full flex flex-col p-4">
      {/* Hidden file input for Excel import */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".xlsx,.xls"
        className="hidden"
      />

      <PermissionsContainer componentId="equipment-list-table" componentName="Equipment List Table">
        <TanTable
          data={dataWithLocationLabels}
          columns={columns}
          loading={loading}
          settingsKey="equipment-list"
          fieldName="equipment-list-table"
          searchPlaceholder="Search equipment by any field..."
          defaultGlobalFilter={defaultSearch}
          enableColumnFilters={true}
          cardTitle={`Equipment List - ${schema.toUpperCase()}`}
          onRowClick={(row) => {
            if (!editingRowId && !isSaving) {
              handleEditRow(row);
            }
          }}
          getRowClassName={(row, m) => deletedRowClass(row['Deleted'] === 'Y', m.zebra)}
          showDeletedToggle={true}
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          enableZebraStriping={true}
          lockedColumns={[
            'Unit #',
            { id: 'actions', side: 'right' }
          ]}
          onEditFilters={() => setIsAddDrawerOpen(true)}
          showEditFiltersButton={true}
          editFiltersButtonIcon="plus"
          editFiltersButtonTooltip="Add New Equipment"
          onBulkEdit={handleEquipmentBulkEdit}
        />
      </PermissionsContainer>

      {/* Add New Equipment Drawer */}
      <Drawer
        isOpen={isAddDrawerOpen}
        onClose={closeAddDrawer}
        title="Add New Equipment"
        position="right"
        maxWidth="lg"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={closeAddDrawer}
              disabled={isAddSaving}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddEquipment}
              disabled={isAddSaving}
              className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
            >
              {isAddSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  Create Equipment
                </>
              )}
            </button>
          </div>
        }
      >
        <PermissionsContainer componentId="equipment-list-add-form" componentName="Add Equipment Form">
          <div className="space-y-6">
            {/* Identification */}
            <div>
              <h3 className="text-sm font-semibold text-heading mb-3 border-b border-gray-200 dark:border-gray-700 pb-1">Identification</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-label mb-1">Unit # <span className="text-red-500">*</span></label>
                  <PermissionAwareInput type="text" value={addFormData['Unit #'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Unit #': e.target.value }))} disabled={isAddSaving} placeholder="e.g., GWIZ123456" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Category <span className="text-red-500">*</span></label>
                    <PermissionAwareSelect value={addFormData['Category'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Category': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
                      <option value="">Select...</option>
                      <option value="CHASSIS">CHASSIS</option>
                      <option value="FLATBED">FLATBED</option>
                      <option value="MULE">MULE</option>
                      <option value="VAN">VAN</option>
                    </PermissionAwareSelect>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">In Service</label>
                    <PermissionAwareSelect value={addFormData['In Service'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'In Service': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
                      <option value="">Select...</option>
                      <option value="Y">Y</option>
                      <option value="N">N</option>
                      <option value="STACKED">STACKED</option>
                      <option value="STORAGE">STORAGE</option>
                    </PermissionAwareSelect>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Market</label>
                    <PermissionAwareSelect value={addFormData['Market'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Market': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
                      <option value="">Select...</option>
                      {locationOptions.map(loc => (
                        <option key={loc.value} value={loc.value}>{loc.label}</option>
                      ))}
                    </PermissionAwareSelect>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Company</label>
                    <PermissionAwareInput type="text" value={addFormData['Company'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Company': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-label mb-1">Whse Dedicated</label>
                  <PermissionAwareInput type="text" value={addFormData['Whse Dedicated'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Whse Dedicated': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                </div>
              </div>
            </div>

            {/* Vehicle Information */}
            <div>
              <h3 className="text-sm font-semibold text-heading mb-3 border-b border-gray-200 dark:border-gray-700 pb-1">Vehicle Information</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Year</label>
                    <PermissionAwareInput type="text" value={addFormData['Year'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Year': e.target.value }))} disabled={isAddSaving} placeholder="2024" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Make</label>
                    <PermissionAwareInput type="text" value={addFormData['Make'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Make': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Model</label>
                    <PermissionAwareInput type="text" value={addFormData['Model'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Model': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-label mb-1">VIN</label>
                  <PermissionAwareInput type="text" value={addFormData['VIN'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'VIN': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Licence</label>
                    <PermissionAwareInput type="text" value={addFormData['Licence'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Licence': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">State</label>
                    <PermissionAwareInput type="text" value={addFormData['State'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'State': e.target.value }))} disabled={isAddSaving} maxLength={2} placeholder="TX" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Condition</label>
                    <PermissionAwareSelect value={addFormData['Condition'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Condition': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
                      <option value="">Select...</option>
                      <option value="LEASED">LEASED</option>
                      <option value="OWNED">OWNED</option>
                      <option value="RENTAL">RENTAL</option>
                    </PermissionAwareSelect>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Refurbished</label>
                    <PermissionAwareInput type="text" value={addFormData['Refurbished'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Refurbished': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div>
              <h3 className="text-sm font-semibold text-heading mb-3 border-b border-gray-200 dark:border-gray-700 pb-1">Dates</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">In Service Date</label>
                    <PermissionAwareInput type="date" value={addFormData['In Service Date'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'In Service Date': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Out of Service Date</label>
                    <PermissionAwareInput type="date" value={addFormData['Out of Service Date'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Out of Service Date': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Date Acquired</label>
                    <PermissionAwareInput type="date" value={addFormData['Date Acquired'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Date Acquired': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Date Terminated</label>
                    <PermissionAwareInput type="date" value={addFormData['Date Terminated'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Date Terminated': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Reg Effective</label>
                    <PermissionAwareInput type="date" value={addFormData['Reg Eff'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Reg Eff': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Reg Expired</label>
                    <PermissionAwareInput type="date" value={addFormData['Reg Exp'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Reg Exp': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Last Inspection</label>
                    <PermissionAwareInput type="date" value={addFormData['Last Inspection'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Last Inspection': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Lease Information */}
            <div>
              <h3 className="text-sm font-semibold text-heading mb-3 border-b border-gray-200 dark:border-gray-700 pb-1">Lease Information</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Lessor</label>
                    <PermissionAwareSelect value={addFormData['Lessor'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Lessor': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
                      <option value="">Select...</option>
                      <option value="GWI">GWI</option>
                      <option value="PENN LEASING">PENN LEASING</option>
                      <option value="TIP">TIP</option>
                      <option value="XTRA">XTRA</option>
                      <option value="TICO">TICO</option>
                      <option value="BRIGGS">BRIGGS</option>
                      <option value="MERRIMAK">MERRIMAK</option>
                      <option value="PREMIER">PREMIER</option>
                      <option value="TRAK">TRAK</option>
                    </PermissionAwareSelect>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Lease Rate</label>
                    <PermissionAwareInput type="text" value={addFormData['Lease Rate'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Lease Rate': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Lease Number</label>
                    <PermissionAwareInput type="text" value={addFormData['Lease Number'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Lease Number': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Lease Crunch Tag</label>
                    <PermissionAwareInput type="text" value={addFormData['Lease Crunch Tag'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Lease Crunch Tag': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Lease Start Date</label>
                    <PermissionAwareInput type="date" value={addFormData['Lease Start Date'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Lease Start Date': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Lease End Date</label>
                    <PermissionAwareInput type="date" value={addFormData['Lease End Date'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Lease End Date': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Period</label>
                    <PermissionAwareInput type="text" value={addFormData['Period'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Period': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Terms</label>
                    <PermissionAwareInput type="text" value={addFormData['Terms'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Terms': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Financial */}
            <div>
              <h3 className="text-sm font-semibold text-heading mb-3 border-b border-gray-200 dark:border-gray-700 pb-1">Financial</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Financed?</label>
                    <PermissionAwareInput type="text" value={addFormData['Financed?'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Financed?': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Bank Name</label>
                    <PermissionAwareInput type="text" value={addFormData['Bank Name'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Bank Name': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Value</label>
                    <PermissionAwareInput type="number" step="0.01" value={addFormData['Value'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Value': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Premium</label>
                    <PermissionAwareInput type="number" step="0.01" value={addFormData['Premium'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Premium': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Market Value</label>
                    <PermissionAwareInput type="number" step="0.01" value={addFormData['Market Value'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Market Value': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Equipment Details */}
            <div>
              <h3 className="text-sm font-semibold text-heading mb-3 border-b border-gray-200 dark:border-gray-700 pb-1">Equipment Details</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">GPS</label>
                    <PermissionAwareSelect value={addFormData['GPS'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'GPS': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
                      <option value="">Select...</option>
                      <option value="NONE">NONE</option>
                      <option value="BLACKBERRY">BLACKBERRY</option>
                      <option value="POWERFLEET">POWERFLEET</option>
                      <option value="SAMSARA">SAMSARA</option>
                    </PermissionAwareSelect>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Title</label>
                    <PermissionAwareSelect value={addFormData['Title'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Title': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
                      <option value="">Select...</option>
                      <option value="OWNED">OWNED</option>
                      <option value="LEASED">LEASED</option>
                    </PermissionAwareSelect>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Tri-Axle</label>
                    <PermissionAwareSelect value={addFormData['Tri-Axle'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Tri-Axle': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
                      <option value="">Select...</option>
                      <option value="Y">Y</option>
                      <option value="N">N</option>
                    </PermissionAwareSelect>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Air Infl.</label>
                    <PermissionAwareSelect value={addFormData['Air Infl.'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Air Infl.': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white">
                      <option value="">Select...</option>
                      <option value="Y">Y</option>
                      <option value="N">N</option>
                    </PermissionAwareSelect>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Customer Dedicated</label>
                    <PermissionAwareInput type="text" value={addFormData['Customer Dedicated'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Customer Dedicated': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Tire Size</label>
                    <PermissionAwareInput type="text" value={addFormData['Tire Size'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Tire Size': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Geir</label>
                    <PermissionAwareInput type="text" value={addFormData['Geir'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Geir': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Tare Weight</label>
                    <PermissionAwareInput type="text" value={addFormData['Tare Weight'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Tare Weight': e.target.value }))} disabled={isAddSaving} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Comments */}
            <div>
              <h3 className="text-sm font-semibold text-heading mb-3 border-b border-gray-200 dark:border-gray-700 pb-1">Other</h3>
              <div>
                <label className="block text-sm font-medium text-label mb-1">Comments</label>
                <PermissionAwareTextarea value={addFormData['Comments'] || ''} onChange={e => setAddFormData(prev => ({ ...prev, 'Comments': e.target.value }))} disabled={isAddSaving} rows={3} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
              </div>
            </div>
          </div>
        </PermissionsContainer>
      </Drawer>

      {/* Error Notification Toast */}
      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}

      {/* Success Notification Toast */}
      {successNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>
      )}

      {/* Action Modals */}
      <PermissionsContainer componentId="equipment-list-actions" componentName="Equipment Actions">
        {/* Confirmation Modal */}
        <InfoModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
          title={confirmModal.title}
          body={<p className="text-sm text-gray-600 dark:text-gray-400">{confirmModal.message}</p>}
          buttons={[
            {
              label: 'Cancel',
              onClick: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
              variant: 'secondary'
            },
            {
              label: 'Confirm',
              onClick: confirmModal.onConfirm,
              variant: 'danger'
            }
          ]}
          maxWidth="sm"
        />

        {/* Duplicate Modal */}
        <InfoModal
          isOpen={duplicateModal.isOpen}
          onClose={() => setDuplicateModal({ isOpen: false, sourceRow: null, newUnitNumber: '' })}
          title="Duplicate Equipment"
          body={
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Enter a new unit number for the duplicated equipment record.
              </p>
              <div>
                <label htmlFor="newUnitNumber" className="block text-sm font-medium text-label mb-1">
                  New Unit #
                </label>
                <PermissionAwareInput
                  id="newUnitNumber"
                  type="text"
                  value={duplicateModal.newUnitNumber}
                  onChange={(e) => setDuplicateModal(prev => ({ ...prev, newUnitNumber: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-heading focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter new unit number"
                  autoFocus
                />
              </div>
            </div>
          }
          buttons={[
            {
              label: 'Cancel',
              onClick: () => setDuplicateModal({ isOpen: false, sourceRow: null, newUnitNumber: '' }),
              variant: 'secondary'
            },
            {
              label: isDuplicating ? 'Duplicating...' : 'Duplicate',
              onClick: handleDuplicateConfirm,
              variant: 'primary',
              disabled: isDuplicating || !duplicateModal.newUnitNumber.trim()
            }
          ]}
          maxWidth="sm"
        />

        {/* Import Results Modal */}
        <InfoModal
        isOpen={importModal.isOpen}
        onClose={() => setImportModal({ isOpen: false, isImporting: false, progress: null, results: null, summary: null })}
        title="Import Equipment from Excel"
        body={
          <div className="space-y-4">
            {importModal.isImporting && importModal.progress ? (
              <div className="space-y-4 py-4">
                {/* Progress indicator */}
                <div className="flex items-center justify-center">
                  <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
                </div>

                {/* Stage indicator */}
                <div className="text-center">
                  <div className="text-sm font-medium text-heading">
                    {importModal.progress.stage === 'reading' && 'Reading Excel File...'}
                    {importModal.progress.stage === 'uploading' && 'Preparing Data...'}
                    {importModal.progress.stage === 'processing' && 'Processing Records...'}
                    {importModal.progress.stage === 'done' && 'Complete!'}
                  </div>
                  <div className="text-xs text-muted mt-1">
                    {importModal.progress.currentUnit}
                  </div>
                </div>

                {/* Progress bar */}
                {importModal.progress.totalRows > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                      <span>Progress</span>
                      <span>{importModal.progress.totalRows} rows</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                      <div
                        className={`h-2.5 rounded-full transition-all duration-300 ${
                          importModal.progress.stage === 'done'
                            ? 'bg-green-500'
                            : 'bg-blue-500 animate-pulse'
                        }`}
                        style={{
                          width: importModal.progress.stage === 'reading' ? '10%' :
                                 importModal.progress.stage === 'uploading' ? '30%' :
                                 importModal.progress.stage === 'processing' ? '70%' : '100%'
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Stage steps */}
                <div className="flex justify-between px-4 pt-2">
                  {['reading', 'uploading', 'processing', 'done'].map((stage, index) => {
                    const stages = ['reading', 'uploading', 'processing', 'done'];
                    const currentIndex = stages.indexOf(importModal.progress!.stage);
                    const isComplete = index < currentIndex;
                    const isCurrent = index === currentIndex;

                    return (
                      <div key={stage} className="flex flex-col items-center">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                          isComplete ? 'bg-green-500 text-white' :
                          isCurrent ? 'bg-blue-500 text-white' :
                          'bg-gray-200 dark:bg-gray-700 text-gray-500'
                        }`}>
                          {isComplete ? '✓' : index + 1}
                        </div>
                        <span className={`text-xs mt-1 ${
                          isCurrent ? 'text-blue-600 dark:text-blue-400 font-medium' :
                          isComplete ? 'text-green-600 dark:text-green-400' :
                          'text-gray-400'
                        }`}>
                          {stage === 'reading' ? 'Read' :
                           stage === 'uploading' ? 'Parse' :
                           stage === 'processing' ? 'Import' : 'Done'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : importModal.summary ? (
              <>
                {/* Summary */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-heading">{importModal.summary.total_rows}</div>
                    <div className="text-xs text-muted">Total Rows</div>
                  </div>
                  <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">{importModal.summary.total_updated}</div>
                    <div className="text-xs text-green-600 dark:text-green-400">Updated</div>
                  </div>
                  <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{importModal.summary.total_created}</div>
                    <div className="text-xs text-blue-600 dark:text-blue-400">Created</div>
                  </div>
                  <div className="bg-red-100 dark:bg-red-900/30 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">{importModal.summary.total_errors}</div>
                    <div className="text-xs text-red-600 dark:text-red-400">Errors</div>
                  </div>
                </div>

                {/* Results list */}
                {importModal.results && importModal.results.length > 0 && (
                  <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-label">Unit #</th>
                          <th className="px-3 py-2 text-left font-medium text-label">Status</th>
                          <th className="px-3 py-2 text-left font-medium text-label">Message</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {importModal.results.map((result, index) => (
                          <tr key={index} className={result.success ? '' : 'bg-red-50 dark:bg-red-900/10'}>
                            <td className="px-3 py-2 text-heading font-medium">{result.unit_number}</td>
                            <td className="px-3 py-2">
                              {result.success ? (
                                result.created ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-info text-info-fg">
                                    Created
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ok text-ok-fg">
                                    {result.fields_updated > 0 ? `${result.fields_updated} updated` : 'No changes'}
                                  </span>
                                )
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-danger text-danger-fg">
                                  Error
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">{result.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">Select an Excel file to import equipment data.</p>
            )}
          </div>
        }
        buttons={[
          {
            label: 'Close',
            onClick: () => setImportModal({ isOpen: false, isImporting: false, progress: null, results: null, summary: null }),
            variant: 'primary',
            disabled: importModal.isImporting
          }
        ]}
        maxWidth="lg"
        />
      </PermissionsContainer>

      {mapModalData && (
        <ChassisLocationMapModal
          unit={mapModalData.unit}
          lat={mapModalData.lat}
          lng={mapModalData.lng}
          onClose={() => setMapModalData(null)}
        />
      )}
    </div>
  );
};

export default EquipmentList;
